import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Box,
  CircularProgress,
  IconButton,
  Collapse,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomButton from '../../components/buttons/CustomButton';
import styles from './FiscalInvoiceReviewModal.module.css';

/**
 * Calcula a idade em anos a partir de uma string de data de nascimento.
 * Aceita formatos: "YYYY-MM-DD", "YYYY-MM-DD HH:MM:SS", "DD/MM/YYYY"
 * Retorna null se a data for inválida ou ausente.
 */
function calcularIdade(dataNascimento) {
  if (!dataNascimento) return null;
  try {
    let dateStr = dataNascimento.trim();
    let d;
    // Formato DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('/');
      d = new Date(`${year}-${month}-${day}`);
    } else {
      // Formato ISO: YYYY-MM-DD ou YYYY-MM-DD HH:MM:SS
      d = new Date(dateStr.replace(' ', 'T'));
    }
    if (isNaN(d.getTime())) return null;
    const hoje = new Date();
    let idade = hoje.getFullYear() - d.getFullYear();
    const mesAtual = hoje.getMonth();
    const mesNasc = d.getMonth();
    if (mesAtual < mesNasc || (mesAtual === mesNasc && hoje.getDate() < d.getDate())) {
      idade--;
    }
    return idade;
  } catch {
    return null;
  }
}

/**
 * Valida CPF (11 dígitos) ou CNPJ (14 dígitos) — apenas dígitos.
 * Retorna null se válido, ou string de erro se inválido.
 */
function validarDocumento(doc) {
  if (!doc) return 'CPF/CNPJ é obrigatório.';
  const digits = doc.replace(/\D/g, '');
  if (digits.length === 11) return null; // CPF
  if (digits.length === 14) return null; // CNPJ
  return 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos (apenas números).';
}

/**
 * FiscalInvoiceReviewModal
 *
 * Props:
 *   open              — boolean, controla visibilidade
 *   onClose           — callback ao fechar sem emitir
 *   onConfirm         — callback(formData) ao confirmar emissão
 *   issuing           — boolean, indica que a emissão está em andamento
 *   appointmentData   — objeto do agendamento
 *   patientData       — objeto do paciente (name, cpf, cnpj, email, birthday)
 *   fiscalSettings    — configurações fiscais da empresa
 *   serviceDescription — descrição pré-preenchida (já com template resolvido)
 */
function FiscalInvoiceReviewModal({
  open,
  onClose,
  onConfirm,
  issuing = false,
  appointmentData,
  patientData,
  fiscalSettings,
  serviceDescription: initialDescription,
}) {
  // ─── Detectar se o paciente tem responsável financeiro ───────────────────────
  const hasFinancialResponsible = !!(patientData?.financial_responsible_name);

  // ─── Detectar menor de idade ─────────────────────────────────────────────────
  // Tenta os campos mais comuns de data de nascimento no patientData
  const dataNascimento =
    patientData?.birthday ||
    patientData?.birthDate ||
    patientData?.birth_date ||
    patientData?.patientBirthDate ||
    patientData?.dateOfBirth ||
    null;

  const idade = useMemo(() => calcularIdade(dataNascimento), [dataNascimento]);
  const isMenorDeIdade = idade !== null && idade < 18;

  // ─── Dados do paciente e responsável financeiro ──────────────────────────────
  const dadosPaciente = {
    nome: patientData?.name || '',
    documento: patientData?.cpf || patientData?.cnpj || '',
    email: patientData?.email || '',
  };

  const dadosResponsavel = {
    nome: patientData?.financial_responsible_name || '',
    documento: patientData?.financial_responsible_document || '',
    email: patientData?.financial_responsible_email || '',
  };

  // ─── Estado para seleção do tomador ───────────────────────────────────────────
  const [tomadorTipo, setTomadorTipo] = useState(
    hasFinancialResponsible ? 'financial_responsible' : 'patient'
  );

  // ─── Estado inicial baseado no tipo de tomador ───────────────────────────────
  const tomadorInicial = hasFinancialResponsible ? dadosResponsavel : dadosPaciente;

  // ─── Estado do formulário ────────────────────────────────────────────────────
  const valorInicial = appointmentData?.price ? String(appointmentData.price) : '';

  const [nome, setNome] = useState(tomadorInicial.nome);
  const [documento, setDocumento] = useState(tomadorInicial.documento);
  const [email, setEmail] = useState(tomadorInicial.email);
  const [descricao, setDescricao] = useState(initialDescription || '');
  const [valor, setValor] = useState(valorInicial);
  const [codigoServico, setCodigoServico] = useState(
    fiscalSettings?.default_service_code || ''
  );
  const [sendEmail, setSendEmail] = useState(false);

  // Seção técnica colapsável
  const [tecnicoAberto, setTecnicoAberto] = useState(false);

  // ─── Erros de validação ──────────────────────────────────────────────────────
  const [erros, setErros] = useState({});

  // ─── Função para trocar o tomador ────────────────────────────────────────────
  const handleTomadorChange = (tipo) => {
    setTomadorTipo(tipo);
    if (tipo === 'patient') {
      setNome(dadosPaciente.nome);
      setDocumento(dadosPaciente.documento);
      setEmail(dadosPaciente.email);
    } else if (tipo === 'financial_responsible') {
      setNome(dadosResponsavel.nome);
      setDocumento(dadosResponsavel.documento);
      setEmail(dadosResponsavel.email);
    } else {
      // 'other' — limpar para preenchimento manual
      setNome('');
      setDocumento('');
      setEmail('');
    }
    // Limpar erros ao trocar
    setErros({});
  };

  // ─── Aviso de valor diferente ────────────────────────────────────────────────
  const valorOriginal = appointmentData?.price ? parseFloat(appointmentData.price) : null;
  const valorAtual = parseFloat(valor);
  const valorDiferente =
    valorOriginal !== null &&
    !isNaN(valorAtual) &&
    valorAtual !== valorOriginal;

  // ─── Validação e envio ───────────────────────────────────────────────────────
  const handleConfirm = () => {
    const novosErros = {};

    if (!nome.trim()) {
      novosErros.nome = 'Nome / Razão social é obrigatório.';
    }

    const erroDoc = validarDocumento(documento);
    if (erroDoc) {
      novosErros.documento = erroDoc;
    }

    if (!descricao.trim()) {
      novosErros.descricao = 'Descrição do serviço é obrigatória.';
    }

    const valorNum = parseFloat(valor);
    if (!valor || isNaN(valorNum) || valorNum <= 0) {
      novosErros.valor = 'O valor deve ser maior que zero.';
    }

    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) return;

    // Montar payload com overrides
    const docDigits = documento.replace(/\D/g, '');
    onConfirm({
      tomador: {
        name: nome.trim(),
        document: docDigits,
        email: email.trim() || null,
      },
      serviceDescription: descricao.trim(),
      amount: valorNum,
      serviceCode: codigoServico.trim() || undefined,
      sendEmail,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={!issuing ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px',
        },
      }}
    >
      {/* Título */}
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Revisar dados da nota fiscal
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={issuing}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ── Alerta de menor de idade ── */}
          {isMenorDeIdade && (
            <div className={styles.alertaMenorIdade}>
              <InfoOutlinedIcon className={styles.alertaIcone} />
              <div className={styles.alertaMenorIdadeContent}>
                <span>
                  Paciente menor de idade. Verifique se a nota deve ser emitida em nome do responsável.
                </span>
                {!hasFinancialResponsible && patientData?.id && (
                  <a
                    href={`/patients/${patientData.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.alertaAdicionarResponsavel}
                  >
                    + Adicionar responsável financeiro
                  </a>
                )}
              </div>
            </div>
          )}

          {/* ── Seletor de tomador (só aparece quando há responsável financeiro) ── */}
          {hasFinancialResponsible && (
            <div className={styles.tomadorSelector}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Emitir nota para
              </Typography>
              <div className={styles.tomadorOptions}>
                <label className={`${styles.tomadorOption} ${tomadorTipo === 'patient' ? styles.tomadorOptionSelected : ''}`}>
                  <input
                    type="radio"
                    name="tomador"
                    value="patient"
                    checked={tomadorTipo === 'patient'}
                    onChange={() => handleTomadorChange('patient')}
                    disabled={issuing}
                    className={styles.tomadorRadio}
                  />
                  <div className={styles.tomadorOptionContent}>
                    <span className={styles.tomadorOptionLabel}>Paciente</span>
                    <span className={styles.tomadorOptionDetail}>{patientData?.name}</span>
                  </div>
                </label>

                <label className={`${styles.tomadorOption} ${tomadorTipo === 'financial_responsible' ? styles.tomadorOptionSelected : ''}`}>
                  <input
                    type="radio"
                    name="tomador"
                    value="financial_responsible"
                    checked={tomadorTipo === 'financial_responsible'}
                    onChange={() => handleTomadorChange('financial_responsible')}
                    disabled={issuing}
                    className={styles.tomadorRadio}
                  />
                  <div className={styles.tomadorOptionContent}>
                    <span className={styles.tomadorOptionLabel}>Responsável financeiro</span>
                    <span className={styles.tomadorOptionDetail}>
                      {patientData?.financial_responsible_name}
                      {patientData?.financial_responsible_relationship && (
                        <span className={styles.tomadorRelationship}> · {patientData.financial_responsible_relationship}</span>
                      )}
                    </span>
                  </div>
                </label>

                <label className={`${styles.tomadorOption} ${tomadorTipo === 'other' ? styles.tomadorOptionSelected : ''}`}>
                  <input
                    type="radio"
                    name="tomador"
                    value="other"
                    checked={tomadorTipo === 'other'}
                    onChange={() => handleTomadorChange('other')}
                    disabled={issuing}
                    className={styles.tomadorRadio}
                  />
                  <div className={styles.tomadorOptionContent}>
                    <span className={styles.tomadorOptionLabel}>Outro</span>
                    <span className={styles.tomadorOptionDetail}>Preencher manualmente</span>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* ── Seção 1: Dados do tomador ── */}
          <div className={styles.secao}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Dados do tomador
            </Typography>

            {/* Nome */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Nome / Razão social <span className={styles.obrigatorio}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.input} ${erros.nome ? styles.inputErro : ''}`}
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                  if (erros.nome) setErros((prev) => ({ ...prev, nome: undefined }));
                }}
                placeholder="Nome completo ou razão social"
                disabled={issuing}
              />
              {erros.nome && <span className={styles.mensagemErro}>{erros.nome}</span>}
            </div>

            {/* CPF / CNPJ */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                CPF / CNPJ <span className={styles.obrigatorio}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.input} ${erros.documento ? styles.inputErro : ''}`}
                value={documento}
                onChange={(e) => {
                  setDocumento(e.target.value);
                  if (erros.documento) setErros((prev) => ({ ...prev, documento: undefined }));
                }}
                placeholder="Somente números (11 para CPF, 14 para CNPJ)"
                disabled={issuing}
              />
              {erros.documento && (
                <span className={styles.mensagemErro}>{erros.documento}</span>
              )}
            </div>

            {/* E-mail */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>E-mail (opcional)</label>
              <input
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                disabled={issuing}
              />
            </div>
          </div>

          <Divider />

          {/* ── Seção 2: Dados da nota ── */}
          <div className={styles.secao}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Dados da nota
            </Typography>

            {/* Descrição do serviço */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Descrição do serviço <span className={styles.obrigatorio}>*</span>
              </label>
              <textarea
                className={`${styles.textarea} ${erros.descricao ? styles.inputErro : ''}`}
                value={descricao}
                onChange={(e) => {
                  setDescricao(e.target.value);
                  if (erros.descricao) setErros((prev) => ({ ...prev, descricao: undefined }));
                }}
                rows={3}
                placeholder="Descrição do serviço prestado"
                disabled={issuing}
              />
              {erros.descricao && (
                <span className={styles.mensagemErro}>{erros.descricao}</span>
              )}
            </div>

            {/* Valor */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Valor da nota (R$) <span className={styles.obrigatorio}>*</span>
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                className={`${styles.input} ${erros.valor ? styles.inputErro : ''}`}
                value={valor}
                onChange={(e) => {
                  setValor(e.target.value);
                  if (erros.valor) setErros((prev) => ({ ...prev, valor: undefined }));
                }}
                placeholder="0,00"
                disabled={issuing}
              />
              {erros.valor && <span className={styles.mensagemErro}>{erros.valor}</span>}
            </div>

            {/* Aviso de valor diferente */}
            {valorDiferente && (
              <div className={styles.alertaValorDiferente}>
                <WarningAmberOutlinedIcon fontSize="small" />
                <span>O valor da nota é diferente do valor registrado no agendamento.</span>
              </div>
            )}

            {/* Checkbox enviar e-mail */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  size="small"
                  disabled={issuing}
                />
              }
              label={
                <Typography variant="body2">
                  Enviar nota por e-mail ao paciente após emissão
                </Typography>
              }
              sx={{ mt: 0.5, mb: 0 }}
            />
          </div>

          <Divider />

          {/* ── Seção 3: Dados técnicos (colapsável) ── */}
          <div className={styles.secao}>
            <button
              type="button"
              className={styles.botaoColapsavel}
              onClick={() => setTecnicoAberto((prev) => !prev)}
              disabled={issuing}
            >
              <Typography variant="subtitle2" fontWeight={600} component="span">
                Dados técnicos
              </Typography>
              {tecnicoAberto ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </button>

            <Collapse in={tecnicoAberto}>
              <div className={styles.secaoTecnica}>
                {/* Código de serviço */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Código de serviço</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={codigoServico}
                    onChange={(e) => setCodigoServico(e.target.value)}
                    placeholder="Ex: 04391"
                    disabled={issuing}
                  />
                </div>

                {/* Resumo das configurações fiscais */}
                {fiscalSettings && (
                  <div className={styles.resumoFiscal}>
                    <div className={styles.resumoRow}>
                      <span>Alíquota ISS:</span>
                      <strong>
                        {fiscalSettings.default_aliquot
                          ? `${fiscalSettings.default_aliquot}%`
                          : '—'}
                      </strong>
                    </div>
                    <div className={styles.resumoRow}>
                      <span>Ambiente:</span>
                      <strong>
                        {fiscalSettings.environment === 'producao'
                          ? 'Produção'
                          : 'Homologação (testes)'}
                      </strong>
                    </div>
                  </div>
                )}

                {/* Alerta de homologação */}
                {fiscalSettings?.environment === 'homologacao' && (
                  <Alert severity="warning" icon={<WarningAmberOutlinedIcon />} sx={{ mt: 1 }}>
                    <strong>Ambiente de Homologação:</strong> Esta nota não terá validade fiscal.
                  </Alert>
                )}
              </div>
            </Collapse>
          </div>

        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
          <CustomButton
            variant="outlined"
            style="tertiary"
            label="Cancelar"
            onClick={onClose}
            enabled={!issuing}
          />
          <CustomButton
            variant="contained"
            style="secondary"
            label={issuing ? 'Emitindo...' : 'Emitir nota fiscal'}
            icon={
              issuing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ReceiptLongOutlinedIcon />
              )
            }
            onClick={handleConfirm}
            enabled={!issuing}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default FiscalInvoiceReviewModal;
